import { Injectable, inject } from '@angular/core';
import { IdbStorageService, IDB_STORE_CRYPTO_KEYS } from './idb-storage.service';

/**
 * Storage key for the device-bound AES key inside {@link IDB_STORE_CRYPTO_KEYS}.
 *
 * @remarks
 * Safe to store alongside nothing else sensitive precisely because the stored
 * value is a non-extractable `CryptoKey` (see {@link DeviceCryptoService}),
 * never raw key bytes a reader of this store could exfiltrate.
 */
const AES_KEY_STORAGE_KEY = '__device_aes_key__';

/** AES-GCM key length in bits. 256 is the standard "high" AES-GCM strength. */
const AES_KEY_LENGTH_BITS = 256;

/**
 * AES-GCM initialization-vector length in bytes.
 *
 * @remarks
 * 96 bits (12 bytes) is the length NIST SP 800-38D recommends for GCM and the
 * one every major implementation optimizes for — using a different length is
 * supported but not recommended, and would gain nothing here.
 */
const IV_LENGTH_BYTES = 12;

/** An AES-GCM–encrypted value, ready to persist via `IdbStorageService`. */
export interface EncryptedPayload {
  /** Random, single-use initialization vector for this ciphertext. */
  iv: Uint8Array<ArrayBuffer>;
  /** AES-GCM output — ciphertext with the authentication tag appended. */
  ciphertext: Uint8Array<ArrayBuffer>;
}

/**
 * Checks whether a value is a `Uint8Array`, without `instanceof`.
 *
 * @remarks
 * **`instanceof Uint8Array` is realm-fragile — confirmed to actually fail,
 * not just theoretically.** A typed array built by one JS realm's
 * `TextEncoder`/`crypto.subtle` isn't `instanceof` a *different* realm's
 * `Uint8Array` constructor, because `instanceof` walks the prototype chain by
 * object identity, and each realm has its own distinct built-ins. This bit
 * `isEncryptedPayload()` directly during test development: under
 * `jest-environment-jsdom`, `TextEncoder` resolves to Node's own
 * implementation while the bare `Uint8Array` identifier resolves to jsdom's
 * separate one — so `new TextEncoder().encode(x) instanceof Uint8Array` is
 * `false` even though the value is a completely genuine `Uint8Array`. The same
 * class of mismatch can happen for real in production wherever a typed array
 * crosses a realm boundary (e.g. structured-cloned out of a Web Worker).
 * `ArrayBuffer.isView()` is the spec-sanctioned, realm-agnostic replacement —
 * it inspects the internal "is a typed-array view" slot rather than walking a
 * prototype chain — and `constructor.name` is a plain string compare, equally
 * realm-safe.
 *
 * @param value - The value to check.
 */
function isUint8Array(value: unknown): value is Uint8Array<ArrayBuffer> {
  return ArrayBuffer.isView(value) && value.constructor.name === 'Uint8Array';
}

/**
 * Type guard distinguishing an {@link EncryptedPayload} from a plaintext
 * string left behind by a pre-encryption build.
 *
 * @remarks
 * Needed because {@link IdbStorageService.get} is untyped storage — the same
 * key can legitimately hold either shape depending on which app version wrote
 * it, and callers like `SignatureStorageService.get()` must handle both (see
 * its own doc comment for the self-healing migration this enables).
 *
 * @param value - The raw value read from storage.
 */
export function isEncryptedPayload(value: unknown): value is EncryptedPayload {
  return (
    !!value &&
    typeof value === 'object' &&
    'iv' in value &&
    'ciphertext' in value &&
    isUint8Array((value as EncryptedPayload).iv) &&
    isUint8Array((value as EncryptedPayload).ciphertext)
  );
}

/**
 * Encrypts/decrypts sensitive values at rest in IndexedDB, using a
 * device-bound, non-extractable AES-GCM key.
 *
 * @remarks
 * **Why this exists (2026-08-05, generalized from a signature-only service of
 * the same design the same day).** IndexedDB adds no protection of its own —
 * unlike native's OS-backed Keychain/Keystore, a plaintext value there is
 * readable by anyone who opens DevTools → Application → IndexedDB, or pulls it
 * from a device/browser-profile backup. This service closes that gap for any
 * web-only value that needs it. Current consumers:
 * - `SignatureStorageService` — the officer's reusable saved-signature image.
 * - `AffidavitAdapter` / `UpqueueAdapter` — the signed affidavit document
 *   queued in `IDB_STORE_QUEUE` (see `AffidavitAdapter.submit()`'s doc
 *   comment). That document already carries ONE layer of encryption
 *   (`CryptoService`'s random per-document key, wrapped with the build-embedded
 *   `CRYPTOJSKEY`, for the SERVER to decrypt on receipt) — but that layer adds
 *   no AT-REST protection locally, because the wrapped key travels in the same
 *   queue entry as the ciphertext, and `CRYPTOJSKEY` itself is a plain string
 *   in the shipped bundle anyone inspecting IndexedDB can equally read. This
 *   service adds the local-only layer that scheme is missing.
 *
 * **Why the key is a non-extractable `CryptoKey`, not a string constant.**
 * The threat this defends against is someone opening DevTools → Application →
 * IndexedDB and reading the stored value directly, or pulling it from a
 * device/browser-profile backup. A string key baked into the shipped JS bundle
 * (e.g. reusing `CryptoService`'s `environment.CRYPTOJSKEY`) would NOT defend
 * against that: anyone who can read IndexedDB in DevTools can equally read the
 * bundle's source and recover the same key, so encrypting with it is no
 * stronger than the plaintext it replaces — this is exactly the gap described
 * above for the affidavit document's existing server-facing encryption.
 * `crypto.subtle.generateKey(..., extractable: false, ...)` produces a
 * `CryptoKey` that can be *used* for encrypt/decrypt but never read out as raw
 * bytes — not by this code, not by a DevTools inspector — which mirrors how
 * the two native backends actually behave: iOS Keychain and Android's
 * `AndroidKeyStore`-backed RSA key (see `SecureStoragePlugin`'s own source —
 * confirmed by reading it directly) both keep their key material inside the OS
 * and only ever hand the app a usable handle, never the key itself. `CryptoKey`
 * objects are structured-cloneable, so they persist in IndexedDB the same way
 * any other value does.
 *
 * **What this does NOT defend against:** arbitrary JavaScript executing in
 * the same page (an XSS payload) — that code runs with the same access this
 * service has and could call `crypto.subtle.decrypt` itself. No client-side
 * scheme, with any key strategy, can close that gap; it isn't a limitation
 * specific to this implementation.
 *
 * The key is generated once per device/browser-profile (lazily, on first use)
 * and reused for EVERY value any consumer encrypts — it has no association
 * with any particular user, feature, or piece of data, only with the physical
 * storage. Sharing one key across consumers/entities doesn't weaken isolation
 * between them, since the key alone reveals nothing about any plaintext
 * without also knowing which specific ciphertext to decrypt, and each
 * encryption uses its own random IV (see {@link encrypt}).
 */
@Injectable({ providedIn: 'root' })
export class DeviceCryptoService {
  private readonly idb = inject(IdbStorageService);

  /**
   * Caches the in-flight/loaded key at the instance level so concurrent
   * `encrypt()`/`decrypt()` calls never race to generate two different keys —
   * `DeviceCryptoService` is a singleton (`providedIn: 'root'`), so one
   * promise is enough to serialise every caller within a single page load.
   * (A genuine cross-tab race — two tabs both generating a key for the first
   * time at once — is a rare, low-severity edge case: the loser's next
   * decrypt attempt simply fails and the value is re-saved.)
   */
  private keyPromise: Promise<CryptoKey> | null = null;

  /**
   * Encrypts a plaintext string with the device-bound key, generating a
   * fresh random IV for this call.
   *
   * @remarks
   * A **new random IV every call** is mandatory for AES-GCM — reusing an IV
   * with the same key catastrophically breaks its confidentiality guarantees.
   * The IV is not secret and is stored alongside the ciphertext so `decrypt()`
   * can use it back.
   *
   * @param plaintext - The value to encrypt.
   * @returns The IV and ciphertext, ready to persist together.
   */
  async encrypt(plaintext: string): Promise<EncryptedPayload> {
    const key = await this.getOrCreateKey();
    const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
    const data = new TextEncoder().encode(plaintext);
    const ciphertext = new Uint8Array(
      await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data),
    );
    return { iv, ciphertext };
  }

  /**
   * Decrypts a payload produced by {@link encrypt}.
   *
   * @remarks
   * Rejects if the ciphertext or IV don't match what the current device key
   * produced (wrong/corrupted data, or — in principle — a payload from a
   * different device's key, which can't happen here since the key never
   * leaves the device). Callers should treat a rejection as "unreadable, not
   * necessarily corrupt" and fall back gracefully rather than surface it as a
   * hard error — see `SignatureStorageService.get()` and
   * `UpqueueAdapter.transmitAffidavit()` for the two established conventions
   * (treat as absent; treat as a transient, retry-next-cycle failure).
   *
   * @param payload - The IV/ciphertext pair from {@link encrypt}.
   * @returns The original plaintext.
   */
  async decrypt(payload: EncryptedPayload): Promise<string> {
    const key = await this.getOrCreateKey();
    const plainBuf = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: payload.iv },
      key,
      payload.ciphertext,
    );
    return new TextDecoder().decode(plainBuf);
  }

  /**
   * Loads the persisted device key, generating and persisting a new one on
   * first use.
   *
   * @remarks
   * Reads/writes {@link IDB_STORE_CRYPTO_KEYS} directly via `IdbStorageService`
   * (not the `getOrMigrate`/`setOrFallback` bridge helpers) — there is no
   * legacy SecureStorage equivalent to migrate for a value that never existed
   * before this feature, and a `CryptoKey` object can't be serialised through
   * those helpers' string-based legacy fallback in the first place.
   *
   * If IndexedDB itself is unavailable, this throws — there is no
   * non-IndexedDB place a non-extractable `CryptoKey` can be persisted across
   * page loads, so every caller must be prepared to catch this and fall back
   * (e.g. `SignatureStorageService.save()`'s plaintext `SecureStoragePlugin`
   * fallback).
   */
  private getOrCreateKey(): Promise<CryptoKey> {
    this.keyPromise ??= this.loadOrGenerateKey();
    return this.keyPromise;
  }

  private async loadOrGenerateKey(): Promise<CryptoKey> {
    const existing = await this.idb.get<CryptoKey>(IDB_STORE_CRYPTO_KEYS, AES_KEY_STORAGE_KEY);
    if (existing) return existing;

    const key = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: AES_KEY_LENGTH_BITS },
      false, // extractable: false — see this class's doc comment for why this is the whole point.
      ['encrypt', 'decrypt'],
    );
    await this.idb.set(IDB_STORE_CRYPTO_KEYS, AES_KEY_STORAGE_KEY, key);
    return key;
  }
}
